<template>
    <!-- we need to make sure we actually have some data for the correct time periods -->
    <div
        v-if="Object.keys(groupedNotifications).filter((n) => !hiddenPeriods?.includes(n)).length !== 0"
        class="gap-md col-span-3 flex w-full flex-col place-content-start"
    >
        <div v-for="(group, period) in groupedNotifications" :key="period">
            <template v-if="!hiddenPeriods?.includes(period.toString())">
                <h3 class="pb-sm col-span-3 capitalize">{{ period }}</h3>
                <div
                    v-for="(notification, index) in group"
                    :key="notification.id"
                    @click="$emit('route', notification.id)"
                >
                    <hr v-if="index !== 0" class="my-xs col-span-full border-(--border-subtle)" />
                    <NotificationCard
                        :notification="notification"
                        :creator="creator"
                        :selected="!!selected && selected === notification.id"
                        :creator-id="creatorId"
                        :small="small"
                    />
                </div>
            </template>
        </div>
    </div>
    <div v-else class="gap-sm col-span-3 flex flex-col items-center justify-start">
        <i class="i-no-notifications" />
        <template v-if="hasSubscribed">
            <h2>No new notifications here</h2>
            <p>There are no new notifications here. You have already read all the messages</p>
        </template>
        <Button v-else @click="$router.push({ name: 'subscriptions' })"> Subscribe to see notifications </Button>
    </div>
</template>

<script lang="ts" setup>
import { Button } from '@/components/ui/button';
import { type Notification, type User } from '@/stores/store-interfaces';
import { computed, onMounted, ref, watch, type PropType } from 'vue';
import { groupNotifications } from '@/stores/helpers';
import NotificationCard from '../notifications/notification-card.vue';

defineEmits<{ route: [notificationId: string] }>();

const props = defineProps({
    notifications: {
        type: Array as PropType<(Notification | Notification)[]>,
        required: true,
    },
    filteredPeriods: {
        type: Array as PropType<string[]>,
        required: false,
    },
    small: Boolean,
    creator: { type: Object as PropType<User>, required: false },
    notificationFilter: {
        type: Array as PropType<
            {
                name: string;
                value: number | string;
                selected: boolean;
                type: string;
            }[]
        >,
        required: true,
    },
    ongoing: Boolean,
    selected: String,
    hasSubscribed: { type: Boolean, default: true },
});

const periodTypes = ['ongoing', 'today', 'this week', 'last 2 weeks', 'last month', 'last 2 months', 'scheduled'];

const creatorId = ref<string | undefined>(undefined);

const filteredNotifications = ref<Notification[]>([]);
const hiddenPeriods = ref<string[]>([]);
const groupedNotifications = computed(() => groupNotifications(filteredNotifications.value, undefined));

onMounted(() => {
    if (props.creator) {
        creatorId.value = props.creator.user_id;
    }
});

watch(
    () => props.creator,
    (newVal) => {
        if (newVal) {
            creatorId.value = newVal.user_id;
        }
    },
);

onMounted(() => {
    filteredNotifications.value = props.notifications;
});

watch(
    () => props.notifications,
    () => {
        filteredNotifications.value = props.notifications;
    },
);

watch(
    () => props.notificationFilter,
    (
        filter: {
            name: string;
            value: number | string;
            selected: boolean;
            type: string;
        }[],
    ) => {
        if (filter.length === 0) {
            filteredNotifications.value = props.notifications;
            hiddenPeriods.value = [...(props.filteredPeriods ?? '')];
            return;
        }

        let filtered = props.notifications.filter((notification) => {
            return filter.every((f) => {
                if (f.type === 'type') {
                    return notification.type === f.value;
                } else if (f.type === 'severity') {
                    return notification.severity === f.value;
                }
                return true;
            });
        });

        const startDateFilter = filter.find((f) => f.type === 'start_date');
        if (startDateFilter) {
            const startDate = new Date(startDateFilter.value);

            filtered = filtered.filter(
                (notif) =>
                    notif.startDate.getFullYear() === startDate.getFullYear() &&
                    notif.startDate.getMonth() === startDate.getMonth() &&
                    notif.startDate.getDate() === startDate.getDate(),
            );
        }

        const endDateFilter = filter.find((f) => f.type === 'end_date');
        if (endDateFilter) {
            const endDate = new Date(endDateFilter.value);
            filtered = filtered.filter(
                (notif) =>
                    notif.endDate &&
                    notif.endDate.getFullYear() === endDate.getFullYear() &&
                    notif.endDate.getMonth() === endDate.getMonth() &&
                    notif.endDate.getDate() === endDate.getDate(),
            );
        }

        const overlappingPeriods = ['today', 'this week', 'last 2 weeks', 'last month', 'last 2 months'];

        // We add these to a set making sure we can overlap periods without duplicating them
        const shownPeriods = Array.from(
            new Set(
                filter
                    .filter((f) => f.type === 'dates')
                    .map((f) => (f.value as string).toLowerCase())
                    .flatMap((p) => {
                        const index = overlappingPeriods.indexOf(p);
                        if (index === -1) {
                            return p;
                        } else {
                            return overlappingPeriods.slice(0, index + 1);
                        }
                    }),
            ),
        );

        if (shownPeriods.length > 0) {
            const invertedPeriods = periodTypes.filter((p) => !shownPeriods.includes(p));

            hiddenPeriods.value = [...invertedPeriods];
        } else {
            hiddenPeriods.value = [...(props.filteredPeriods ?? '')];
        }

        filteredNotifications.value = filtered;
    },
    { deep: true },
);
</script>

<style>
@reference '@/assets/styles/default.css';

.tooltip {
    position: relative;

    &::after {
        position: absolute;
        font-weight: var(--fontWeight-light) !important;
        font-size: var(--fontSize-sm);
        width: fit-content;
        z-index: 10;
        display: none;
        font-family: var(--fontFamily-default);
        font-style: normal;
        justify-content: center;
        align-items: center;
        inline-size: min-content;
        line-break: strict;
        content: 'Created by me';
        background-color: var(--color-gray-800);
        color: white;
        bottom: 1.25rem;
        right: 50%;
        transform: translateX(50%);
        padding: 4px;
        width: 6rem;
        border-radius: var(--borderRadius-sm);
    }

    &:hover::after {
        display: flex;
    }
}
</style>
